import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import clinicalTrialsService from "../../services/clinicalTrialsService";

const TrialFormPage = () => {
  const { id } = useParams(); // If id exists, we're editing; if not, we're creating
  const navigate = useNavigate();
  const isEditMode = Boolean(id); // Determine mode

  const [loading, setLoading] = useState(isEditMode); // Only load if editing
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Initial empty form state
  const initialFormState = {
    nctId: "",
    title: "",
    status: "",
    phase: "",
    sponsor: "",
    description: "",
    briefSummary: "",
    eligibilityCriteria: "",
    ckdStage: "",
    targetOrgan: "",
    studyType: "",
    enrollment: "",
    startDate: "",
    completionDate: "",
    gender: "",
    minAge: "",
    maxAge: "",
    ageRange: "",
    acceptsHealthyVolunteers: false,
    locations: "",
    isActive: true,
  };

  const [trialData, setTrialData] = useState(initialFormState);

  // Load trial data only if editing
  useEffect(() => {
    if (isEditMode) {
      loadTrialData();
    }
  }, [id]);

  const loadTrialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await clinicalTrialsService.getTrialById(id);

      if (response.success) {
        const trial = response.data;
        setTrialData({
          nctId: trial.nctId || "",
          title: trial.title || "",
          status: trial.status || "",
          phase: trial.phase || "",
          sponsor: trial.sponsor || "",
          description: trial.description || "",
          briefSummary: trial.briefSummary || "",
          eligibilityCriteria: trial.eligibilityCriteria || "",
          ckdStage: trial.ckdStage || "",
          targetOrgan: trial.targetOrgan || "",
          studyType: trial.studyType || "",
          enrollment: trial.enrollment || "",
          startDate: trial.startDate || "",
          completionDate: trial.completionDate || "",
          gender: trial.gender || "",
          minAge: trial.minAge || "",
          maxAge: trial.maxAge || "",
          ageRange: trial.ageRange || "",
          acceptsHealthyVolunteers: trial.acceptsHealthyVolunteers || false,
          locations: trial.locations || "",
          isActive: trial.isActive !== undefined ? trial.isActive : true,
        });
      } else {
        setError("Failed to load trial data");
      }
    } catch (err) {
      console.error("Error loading trial:", err);
      setError("Error loading trial data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setTrialData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Prepare data for submission
      const dataToSubmit = {
        ...trialData,
      };

      let response;

      if (isEditMode) {
        // Update existing trial
        response = await clinicalTrialsService.updateTrial(id, dataToSubmit);
      } else {
        // Create new trial
        response = await clinicalTrialsService.createTrial(dataToSubmit);
      }

      if (response.success || response.data) {
        setSuccessMessage(
          isEditMode
            ? "Trial updated successfully!"
            : "Trial created successfully!"
        );
        setTimeout(() => {
          navigate("/amyloid/clinicaltrialsearch");
        }, 1500);
      } else {
        setError(
          response.message ||
            `Failed to ${isEditMode ? "update" : "create"} trial`
        );
      }
    } catch (err) {
      console.error("Error saving trial:", err);
      setError(
        `Error ${isEditMode ? "updating" : "creating"} trial. Please try again.`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/amyloid/clinicaltrialsearch");
  };

  // Show loading spinner only when editing and loading data
  if (loading && isEditMode) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ mb: 2 }}
          >
            Back to Search
          </Button>

          <Paper
            elevation={2}
            sx={{
              p: 3,
              background: isEditMode
                ? "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)"
                : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isEditMode ? (
                <EditIcon sx={{ fontSize: 40 }} />
              ) : (
                <AddIcon sx={{ fontSize: 40 }} />
              )}
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {isEditMode
                    ? "Edit Clinical Trial"
                    : "Create New Clinical Trial"}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {isEditMode
                    ? `Trial ID: ${id} • NCT ID: ${trialData.nctId || "N/A"}`
                    : "Add a new clinical trial to the database"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={(e) => e.preventDefault()}>
          {/* Section 1: Basic Information */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "primary.main",
                  mb: 3,
                }}
              >
                <Chip label="1" color="primary" size="small" />
                Basic Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="NCT ID"
                    name="nctId"
                    value={trialData.nctId}
                    onChange={handleInputChange}
                    variant="outlined"
                    helperText="e.g., NCT12345678"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Trial Status"
                    name="status"
                    value={trialData.status}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value="Recruiting">Recruiting</MenuItem>
                    <MenuItem value="Active, not recruiting">
                      Active, Not Recruiting
                    </MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Not yet recruiting">
                      Not Yet Recruiting
                    </MenuItem>
                    <MenuItem value="Terminated">Terminated</MenuItem>
                    <MenuItem value="Suspended">Suspended</MenuItem>
                    <MenuItem value="Withdrawn">Withdrawn</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Trial Title"
                    name="title"
                    value={trialData.title}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Phase"
                    name="phase"
                    value={trialData.phase}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Phase</MenuItem>
                    <MenuItem value="Early Phase 1">Early Phase 1</MenuItem>
                    <MenuItem value="Phase 1">Phase 1</MenuItem>
                    <MenuItem value="Phase 1/Phase 2">Phase 1/Phase 2</MenuItem>
                    <MenuItem value="Phase 2">Phase 2</MenuItem>
                    <MenuItem value="Phase 2/Phase 3">Phase 2/Phase 3</MenuItem>
                    <MenuItem value="Phase 3">Phase 3</MenuItem>
                    <MenuItem value="Phase 4">Phase 4</MenuItem>
                    <MenuItem value="Not Applicable">Not Applicable</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sponsor"
                    name="sponsor"
                    value={trialData.sponsor}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section 2: Amyloidosis-Specific Information */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "success.main",
                  mb: 3,
                }}
              >
                <Chip label="2" color="success" size="small" />
                Amyloidosis-Specific Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="CKD Stage"
                    name="ckdStage"
                    value={trialData.ckdStage}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Stage</MenuItem>
                    <MenuItem value="1">Stage 1</MenuItem>
                    <MenuItem value="2">Stage 2</MenuItem>
                    <MenuItem value="3a">Stage 3a</MenuItem>
                    <MenuItem value="3b">Stage 3b</MenuItem>
                    <MenuItem value="4">Stage 4</MenuItem>
                    <MenuItem value="5">Stage 5</MenuItem>
                    <MenuItem value="5D">Stage 5D (Dialysis-dependent)</MenuItem>
                    <MenuItem value="Multiple">Multiple Stages</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Target Organ"
                    name="targetOrgan"
                    value={trialData.targetOrgan}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Target Organ</MenuItem>
                    <MenuItem value="Cardiac">Cardiac</MenuItem>
                    <MenuItem value="Renal">Renal</MenuItem>
                    <MenuItem value="Neurological">Neurological</MenuItem>
                    <MenuItem value="Hepatic">Hepatic</MenuItem>
                    <MenuItem value="GI">Gastrointestinal</MenuItem>
                    <MenuItem value="Multi-organ">Multi-organ</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section 3: Study Details */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "warning.main",
                  mb: 3,
                }}
              >
                <Chip label="3" color="warning" size="small" />
                Study Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Study Type"
                    name="studyType"
                    value={trialData.studyType}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Study Type</MenuItem>
                    <MenuItem value="Interventional">Interventional</MenuItem>
                    <MenuItem value="Observational">Observational</MenuItem>
                    <MenuItem value="Expanded Access">Expanded Access</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Enrollment"
                    name="enrollment"
                    value={trialData.enrollment}
                    onChange={handleInputChange}
                    variant="outlined"
                    helperText="e.g., '100 patients' or just '100'"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="startDate"
                    value={trialData.startDate}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="e.g., January 2024"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Completion Date"
                    name="completionDate"
                    value={trialData.completionDate}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="e.g., December 2026"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section 4: Eligibility Criteria */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "info.main",
                  mb: 3,
                }}
              >
                <Chip label="4" color="info" size="small" />
                Eligibility Criteria
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Gender"
                    name="gender"
                    value={trialData.gender}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Minimum Age"
                    name="minAge"
                    value={trialData.minAge}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="e.g., 18 Years"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Maximum Age"
                    name="maxAge"
                    value={trialData.maxAge}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="e.g., 75 Years"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Age Range (Alternative)"
                    name="ageRange"
                    value={trialData.ageRange}
                    onChange={handleInputChange}
                    variant="outlined"
                    helperText="e.g., '18-75 Years' - Use this OR Min/Max Age fields above"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trialData.acceptsHealthyVolunteers}
                        onChange={handleInputChange}
                        name="acceptsHealthyVolunteers"
                        color="primary"
                      />
                    }
                    label="Accepts Healthy Volunteers"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Eligibility Criteria"
                    name="eligibilityCriteria"
                    value={trialData.eligibilityCriteria}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={6}
                    helperText="Detailed inclusion and exclusion criteria"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section 5: Descriptions */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "secondary.main",
                  mb: 3,
                }}
              >
                <Chip label="5" color="secondary" size="small" />
                Descriptions
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Brief Summary"
                    name="briefSummary"
                    value={trialData.briefSummary}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={4}
                    helperText="Short overview of the trial"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Detailed Description"
                    name="description"
                    value={trialData.description}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={6}
                    helperText="Comprehensive description of the trial"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section 6: Locations */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "error.main",
                  mb: 3,
                }}
              >
                <Chip label="6" color="error" size="small" />
                Trial Locations
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Locations"
                    name="locations"
                    value={trialData.locations}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    helperText="Enter locations (e.g., 'Boston, MA; New York, NY; Chicago, IL')"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper elevation={3} sx={{ p: 3, position: "sticky", bottom: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  color="success"
                  startIcon={
                    saving ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                  }}
                >
                  {saving
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                    ? "Save Changes"
                    : "Create Trial"}
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  color="inherit"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </form>
      </Box>
    </Container>
  );
};

export default TrialFormPage;
